import React, { useMemo, useState } from "react";
import { useNavigate } from "react-router-dom";
import Select from "react-select";
import { toast } from "react-toastify";
import { useAuthFetch } from "../hooks/useAuthFetch";
import "../stili/MagazzinoForm.css";

const API_URL = process.env.REACT_APP_API_URL;

const categorieOptions = [
  { value: "KIT_GARA", label: "Kit gara" },
  { value: "KIT_ALLENAMENTO", label: "Kit allenamento" },
  { value: "ABBIGLIAMENTO", label: "Abbigliamento" },
  { value: "BORSA", label: "Borsa" },
  { value: "GIUBBOTTO", label: "Giubbotto" },
  { value: "CALZE", label: "Calze" },
  { value: "PARASTINCHI", label: "Parastinchi" },
  { value: "ACCESSORIO", label: "Accessorio" },
  { value: "ALTRO", label: "Altro" }
];

const statoOptions = [
  { value: "ATTIVO", label: "Attivo" },
  { value: "NON_ATTIVO", label: "Non attivo" }
];

const selectStyles = {
  control: (base, state) => ({
    ...base,
    minHeight: 44,
    borderRadius: 14,
    background: "rgba(255,255,255,0.12)",
    borderColor: state.isFocused ? "rgba(255,255,255,0.45)" : "rgba(255,255,255,0.18)",
    boxShadow: "none",
    backdropFilter: "blur(6px)"
  }),
  menu: (base) => ({
    ...base,
    borderRadius: 14,
    overflow: "hidden",
    background: "rgba(18, 31, 70, 0.96)",
    backdropFilter: "blur(10px)",
    zIndex: 9999
  }),
  menuPortal: (base) => ({
    ...base,
    zIndex: 9999
  }),
  singleValue: (base) => ({
    ...base,
    color: "#fff"
  }),
  input: (base) => ({
    ...base,
    color: "#fff"
  }),
  placeholder: (base) => ({
    ...base,
    color: "rgba(255,255,255,0.70)"
  }),
  option: (base, state) => ({
    ...base,
    background: state.isFocused ? "rgba(255,255,255,0.14)" : "transparent",
    color: "#fff",
    cursor: "pointer"
  })
};

const initialForm = {
  codice: "",
  nome: "",
  descrizione: "",
  categoriaArticolo: null,
  taglia: "",
  prezzoAcquisto: "",
  prezzoVendita: "",
  quantitaIniziale: "",
  scortaMinima: "",
  stato: "ATTIVO",
  note: ""
};

const toNumberOrNull = (value) => {
  if (value === "" || value === null || value === undefined) return null;
  const normalized = String(value).replace(",", ".");
  const parsed = Number(normalized);
  return Number.isNaN(parsed) ? null : parsed;
};

export default function MagazzinoForm() {
  const navigate = useNavigate();
  const authFetch = useAuthFetch();

  const [form, setForm] = useState(initialForm);
  const [saving, setSaving] = useState(false);

  const categoriaSelezionata = useMemo(
    () => categorieOptions.find((opt) => opt.value === form.categoriaArticolo) || null,
    [form.categoriaArticolo]
  );

  const statoSelezionato = useMemo(
    () => statoOptions.find((opt) => opt.value === form.stato) || null,
    [form.stato]
  );

  const onChangeField = (e) => {
    const { name, value } = e.target;
    setForm((prev) => ({ ...prev, [name]: value }));
  };

  const resetForm = () => {
    setForm(initialForm);
  };

  const validaForm = () => {
    if (!form.codice.trim()) {
      toast.error("Inserisci il codice articolo");
      return false;
    }
    if (!form.nome.trim()) {
      toast.error("Inserisci il nome articolo");
      return false;
    }
    if (!form.categoriaArticolo) {
      toast.error("Seleziona la categoria articolo");
      return false;
    }

    const prezzoVendita = toNumberOrNull(form.prezzoVendita);
    if (prezzoVendita === null || prezzoVendita < 0) {
      toast.error("Inserisci un prezzo vendita valido");
      return false;
    }

    const quantitaIniziale = toNumberOrNull(form.quantitaIniziale);
    if (quantitaIniziale === null || quantitaIniziale < 0) {
      toast.error("Inserisci una quantità iniziale valida");
      return false;
    }

    const scortaMinima = toNumberOrNull(form.scortaMinima);
    if (scortaMinima === null || scortaMinima < 0) {
      toast.error("Inserisci una scorta minima valida");
      return false;
    }

    const prezzoAcquisto = toNumberOrNull(form.prezzoAcquisto);
    if (prezzoAcquisto !== null && prezzoAcquisto < 0) {
      toast.error("Il prezzo acquisto non può essere negativo");
      return false;
    }

    return true;
  };

  const buildPayload = () => ({
    codice: form.codice.trim(),
    nome: form.nome.trim(),
    descrizione: form.descrizione.trim() || null,
    categoriaArticolo: form.categoriaArticolo,
    taglia: form.taglia.trim() || null,
    prezzoAcquisto: toNumberOrNull(form.prezzoAcquisto),
    prezzoVendita: toNumberOrNull(form.prezzoVendita),
    quantitaIniziale: Number(toNumberOrNull(form.quantitaIniziale)),
    scortaMinima: Number(toNumberOrNull(form.scortaMinima)),
    stato: form.stato || "ATTIVO",
    note: form.note.trim() || null
  });

  const onSubmit = async (e) => {
    e.preventDefault();
    if (!validaForm()) return;

    setSaving(true);
    try {
      const res = await authFetch(`${API_URL}/api/admin/magazzino/articoli`, {
        method: "POST",
        headers: {
          "Content-Type": "application/json"
        },
        body: JSON.stringify(buildPayload())
      });

      if (!res.ok) {
        const msg = await res.text();
        throw new Error(msg || "Errore durante il salvataggio dell'articolo");
      }

      toast.success("Articolo di magazzino creato correttamente");
      resetForm();
    } catch (err) {
      toast.error(err.message || "Errore durante il salvataggio");
    } finally {
      setSaving(false);
    }
  };

  return (
    <div className="sfondo-immagine">
      <div className="magazzino-form-page">
        <div className="magazzino-form-container">
          <div className="magazzino-form-header card-glass">
            <h2 className="magazzino-form-title">Inserisci Articolo Magazzino</h2>
            <p className="magazzino-form-subtitle">
              Crea un nuovo articolo e imposta subito stock iniziale, prezzo e soglia minima.
            </p>
          </div>

          <form className="magazzino-form-grid" onSubmit={onSubmit}>
            <div className="card-glass magazzino-card">
              <h3>Dati articolo</h3>

              <div className="magazzino-fields two-columns">
                <div className="magazzino-field">
                  <label>Codice articolo</label>
                  <input
                    type="text"
                    name="codice"
                    value={form.codice}
                    onChange={onChangeField}
                    placeholder="Es. KIT-GARA-M-001"
                  />
                </div>

                <div className="magazzino-field">
                  <label>Nome articolo</label>
                  <input
                    type="text"
                    name="nome"
                    value={form.nome}
                    onChange={onChangeField}
                    placeholder="Es. Kit gara ufficiale"
                  />
                </div>

                <div className="magazzino-field">
                  <label>Categoria articolo</label>
                  <Select
                    options={categorieOptions}
                    value={categoriaSelezionata}
                    onChange={(opt) =>
                      setForm((prev) => ({
                        ...prev,
                        categoriaArticolo: opt ? opt.value : null
                      }))
                    }
                    placeholder="Seleziona categoria"
                    styles={selectStyles}
                    isClearable
                    menuPortalTarget={document.body}
                    menuPosition="fixed"
                  />
                </div>

                <div className="magazzino-field">
                  <label>Taglia</label>
                  <input
                    type="text"
                    name="taglia"
                    value={form.taglia}
                    onChange={onChangeField}
                    placeholder="Es. XS, S, M, L"
                  />
                </div>

                <div className="magazzino-field magazzino-field-full">
                  <label>Descrizione</label>
                  <textarea
                    name="descrizione"
                    value={form.descrizione}
                    onChange={onChangeField}
                    rows="3"
                    placeholder="Descrizione articolo"
                  />
                </div>
              </div>
            </div>

            <div className="card-glass magazzino-card">
              <h3>Dati economici e stock</h3>

              <div className="magazzino-fields two-columns">
                <div className="magazzino-field">
                  <label>Prezzo acquisto</label>
                  <input
                    type="number"
                    step="0.01"
                    min="0"
                    name="prezzoAcquisto"
                    value={form.prezzoAcquisto}
                    onChange={onChangeField}
                    placeholder="0.00"
                  />
                </div>

                <div className="magazzino-field">
                  <label>Prezzo vendita</label>
                  <input
                    type="number"
                    step="0.01"
                    min="0"
                    name="prezzoVendita"
                    value={form.prezzoVendita}
                    onChange={onChangeField}
                    placeholder="0.00"
                  />
                </div>

                <div className="magazzino-field">
                  <label>Quantità iniziale</label>
                  <input
                    type="number"
                    min="0"
                    name="quantitaIniziale"
                    value={form.quantitaIniziale}
                    onChange={onChangeField}
                    placeholder="0"
                  />
                </div>

                <div className="magazzino-field">
                  <label>Scorta minima</label>
                  <input
                    type="number"
                    min="0"
                    name="scortaMinima"
                    value={form.scortaMinima}
                    onChange={onChangeField}
                    placeholder="0"
                  />
                </div>

                <div className="magazzino-field">
                  <label>Stato</label>
                  <Select
                    options={statoOptions}
                    value={statoSelezionato}
                    onChange={(opt) =>
                      setForm((prev) => ({
                        ...prev,
                        stato: opt ? opt.value : "ATTIVO"
                      }))
                    }
                    placeholder="Seleziona stato"
                    styles={selectStyles}
                    isClearable={false}
                    menuPortalTarget={document.body}
                    menuPosition="fixed"
                  />
                </div>

                <div className="magazzino-field">
                  <label>Note</label>
                  <input
                    type="text"
                    name="note"
                    value={form.note}
                    onChange={onChangeField}
                    placeholder="Note interne"
                  />
                </div>
              </div>
            </div>

            <div className="card-glass magazzino-card magazzino-riepilogo-card">
              <h3>Riepilogo rapido</h3>

              <div className="magazzino-riepilogo">
                <div>
                  <span>Codice</span>
                  <strong>{form.codice?.trim() || "-"}</strong>
                </div>
                <div>
                  <span>Nome</span>
                  <strong>{form.nome?.trim() || "-"}</strong>
                </div>
                <div>
                  <span>Categoria</span>
                  <strong>{categoriaSelezionata?.label || "-"}</strong>
                </div>
                <div>
                  <span>Taglia</span>
                  <strong>{form.taglia?.trim() || "-"}</strong>
                </div>
                <div>
                  <span>Prezzo vendita</span>
                  <strong>{form.prezzoVendita || "0.00"} €</strong>
                </div>
                <div>
                  <span>Stock iniziale</span>
                  <strong>{form.quantitaIniziale || "0"}</strong>
                </div>
              </div>

              <div className="magazzino-actions">
                <button
                  type="button"
                  className="btn-secondario"
                  onClick={resetForm}
                  disabled={saving}
                >
                  Pulisci
                </button>

                <button
                  type="button"
                  className="btn-secondario"
                  onClick={() => navigate("/gestionale")}
                  disabled={saving}
                >
                  Torna al gestionale
                </button>

                <button
                  type="submit"
                  className="btn-primario"
                  disabled={saving}
                >
                  {saving ? "Salvataggio..." : "Salva articolo"}
                </button>
              </div>
            </div>
          </form>
        </div>
      </div>
    </div>
  );
}